import React from 'react';
import {Helmet} from 'react-helmet-async';
import {Link, useOutletContext} from 'react-router-dom';
import GeoPageLayout from '../../components/geo/GeoPageLayout';
import GeoRestaurantList from '../../components/geo/GeoRestaurantList';
import {getGeoCityContent} from '../../data/geoContent.mock';
import {
    buildBreadcrumbListSchema,
    buildRestaurantItemListSchema,
} from '../../data/geoStructuredData';
import {
    GeoCityConfig,
    DEFAULT_OG_IMAGE,
    SITE_ORIGIN,
    absoluteUrl,
    geoCityPath,
    geoBestRestaurantsPath,
} from '../../data/geoRoutes';

const GeoBestRestaurants: React.FC = () => {
    const geo = useOutletContext<GeoCityConfig>();
    const content = getGeoCityContent(geo.restaurantsDataKey);
    const canonicalPath = geoBestRestaurantsPath(geo);
    const canonicalUrl = absoluteUrl(canonicalPath);
    const year = new Date().getFullYear();

    const pageTitle = `Best Restaurants in ${geo.cityName} ${year} | Solt`;
    const pageDescription = `Best restaurants in ${geo.cityName}, ${geo.countryName} (${year}): ranked foodie picks with real dish photos on Solt — The Lobby, Ruka Izakaya, Mom Bucharest, and more.`;

    const restaurants = content?.restaurants ?? [];

    const cityHubUrl = absoluteUrl(geoCityPath(geo));
    const breadcrumbSchema = buildBreadcrumbListSchema([
        {name: 'Solt', item: `${SITE_ORIGIN}/`},
        {name: geo.cityName, item: cityHubUrl},
        {name: 'Best restaurants', item: canonicalUrl},
    ]);
    const restaurantListSchema = buildRestaurantItemListSchema({
        name: `Best restaurants in ${geo.cityName}`,
        restaurants: restaurants.map((r) => ({
            name: r.name,
            description: r.description,
            soltPath: r.soltPath,
        })),
        fallbackUrl: canonicalUrl,
    });

    const structuredData = {
        '@context': 'https://schema.org',
        '@graph': [
            {
                '@type': 'WebPage',
                '@id': `${canonicalUrl}#webpage`,
                url: canonicalUrl,
                name: pageTitle,
                description: pageDescription,
                inLanguage: 'en',
                isPartOf: {'@id': `${SITE_ORIGIN}/#website`},
            },
            breadcrumbSchema,
            ...(restaurantListSchema ? [restaurantListSchema] : []),
        ],
    };

    return (
        <GeoPageLayout geo={geo} breadcrumbLabel="Best restaurants">
            <Helmet>
                <title>{pageTitle}</title>
                <meta name="description" content={pageDescription}/>
                <link rel="canonical" href={canonicalUrl}/>
                <meta property="og:title" content={pageTitle}/>
                <meta property="og:description" content={pageDescription}/>
                <meta property="og:url" content={canonicalUrl}/>
                <meta property="og:type" content="article"/>
                <meta property="og:image" content={DEFAULT_OG_IMAGE}/>
                <meta name="twitter:card" content="summary_large_image"/>
                <meta name="twitter:image" content={DEFAULT_OG_IMAGE}/>
                <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
            </Helmet>

            <article className="geo-article">
                <h1>Best restaurants in {geo.cityName}</h1>
                <p className="geo-page-lead">
                    {content?.restaurantsIntro ??
                        `Discover the best restaurants in ${geo.cityName} — updated for ${year} on Solt.`}
                </p>

                <p>
                    Looking for a broader city guide? See{' '}
                    <Link to={geoCityPath(geo)}>where to eat in {geo.cityName}</Link>.
                </p>

                <section aria-labelledby="ranked-list-heading">
                    <h2 id="ranked-list-heading">
                        Ranked list — best restaurants in {geo.cityName} ({year})
                    </h2>
                    <p>
                        Order reflects current Solt foodie favorites. Tap any venue for dish-level
                        photos and saves in the app.
                    </p>
                    {restaurants.length > 0 ? (
                        <GeoRestaurantList
                            restaurants={restaurants}
                            headingLevel="h3"
                            showRank
                        />
                    ) : (
                        <p>Restaurant list coming soon for {geo.cityName}.</p>
                    )}
                </section>
            </article>
        </GeoPageLayout>
    );
};

export default GeoBestRestaurants;
